/**
 * config.ts – Single Source für die App-Konfiguration.
 *
 * Hier stehen Titel (appName), Logo (logoUrl),
 * Breakpoints und Growl-Defaults. Alle Konsumenten importieren appConfig von hier.
 *
 * Sidebar-Navigation, SidebarFooter, UserMenu und QuickActions kommen vom Server
 * (Class_Navigation → NavigationContext). config.ts enthält nur noch Layout-Konfiguration.
 *
 * Zusammenhänge:
 *   - types.ts              → AppConfig, Breakpoints, GrowlConfig
 *   - App.tsx               → Layout, Logo, Accordion, Favicon-Setup
 *   - LoginPage.tsx         → Logo und Titel auf der Login-Seite
 *   - Sidebar.tsx           → Logo, Breakpoints
 *   - Growl.tsx             → Growl-Konfiguration (Position, Auto-Dismiss)
 *   - NavigationContext.tsx  → Sidebar + Menüs kommen vom Server (nicht mehr hier)
 *
 * appName ändern? Hier anpassen und zusätzlich <title> in index.html setzen (Tab-Titel vor App-Load).
 */
import type { AppConfig, Breakpoints, GrowlConfig } from '@/types';

/** Standard-Breakpoints (px). Schwellen für responsive Layouts: mobile < tablet < desktop < wide. */
export const defaultBreakpoints: Breakpoints = {
  tablet: 768,
  desktop: 1024,
  wide: 1280,
  paginationTextFrom: 768,
  tableStickyLeftFrom: 768,
  viewportRightSideRatio: 0.7,
};

/** Standard-Growl/Toast-Konfiguration: Position, Breite, Auto-Dismiss-Dauer und Abstand. */
export const defaultGrowlConfig: GrowlConfig = {
  top: 56,
  right: 16,
  maxWidth: 384,
  autoDismissMs: 4000,
  gap: 8,
};

/** Vollständige App-Standardkonfiguration. Single Source für Branding und Struktur. */
export const defaultConfig: AppConfig = {
  initialSidebarMode: 'expanded',
  defaultSidebarWidth: 256,
  maxSidebarWidth: 400,
  topNavHeight: 64,
  growl: { ...defaultGrowlConfig },
  breakpoints: { ...defaultBreakpoints },
  sidebarAccordion: true,
  sidebarName: 'Xeno',
  appName: 'Xeno Admin',
  logoUrl: './images/logo.svg',
  // sidebar + sidebarFooter kommen vom Server (Class_Navigation → NavigationContext).
  // Keine statische Definition mehr nötig – Single Source of Truth in PHP.
};

/** Von allen Konsumenten zu verwendende App-Konfiguration (Single Source: diese Datei). */
export const appConfig: AppConfig = defaultConfig;

// ═══════════════════════════════════════════════════════════════════════════════
// Visuelle Identitaet: Gender + Organisation – Fallback-Defaults
// ═══════════════════════════════════════════════════════════════════════════════
//
// Keys = DB-Werte (system_customers.gender / system_dates_signupers.gender TINYINT).
// Farben referenzieren benannte Hue-Tokens (var(--color-blue) etc.) aus tailwind.css.
// deriveColorSet() leitet daraus per CSS color-mix() bg/border/text ab → theme-aware.
//
// Spaeter (Phase 2) koennen diese Werte durch eine DB-Entitaet (system_contacttypes)
// ueberschrieben werden. resolveGenderConfig() akzeptiert dafuer einen optionalen
// configMap-Parameter; ohne configMap greifen diese Fallbacks.
//
// Verwendet von: ItemChip (Rahmen, Badges, SyncBar), StatsBar, DateHeader,
// BookingCounter, GanttView, GuiElements (Demo-Karten).
// ═══════════════════════════════════════════════════════════════════════════════

import type { SpecialTypeConfig } from '@/types';

/**
 * Fallback-Config pro Gender-DB-Wert (0=Maennlich, 1=Weiblich, 2=Divers).
 * Literal-Zahlen als Keys – kein Import von Gender-Enum noetig, kein Import-Zyklus.
 */
export const GENDER_DEFAULTS: Record<number, SpecialTypeConfig> = {
  0: { label: 'Männlich', color: 'var(--color-blue)', icon: 'mars' },
  1: { label: 'Weiblich', color: 'var(--color-pink)', icon: 'venus' },
  2: { label: 'Divers',   color: 'var(--color-purple)', icon: 'non-binary' },
};

/** Neutraler Fallback fuer null/unbekanntes Gender (circle-dot als stilistisch neutrales Symbol). */
export const GENDER_DEFAULT_FALLBACK: SpecialTypeConfig = { label: 'Unbekannt', color: 'var(--color-gray)', icon: 'circle-dot' };

/** Visuelle Identitaet fuer Organisations-Kontakte (system_customers.type=1). */
export const ORGANISATION_DEFAULT: SpecialTypeConfig = { label: 'Organisation', color: 'var(--color-amber)', icon: 'building' };

// ═══════════════════════════════════════════════════════════════════════════════
// Zimmerkategorie-Modus (DEHOGA vs. Vereinfacht)
// ═══════════════════════════════════════════════════════════════════════════════
//
// Zimmerkategorien (EZ/DZ/MBZ) werden nach DEHOGA-Standard aus der vorgesehenen
// Belegung (maxPersons) abgeleitet, NICHT aus der Bettenart:
//   - EZ = maxPersons 1 (egal ob Einzel- oder Doppelbett)
//   - DZ = maxPersons 2 (egal ob Doppelbett oder zwei Einzelbetten / Twin)
//   - MBZ = maxPersons 3+
//
// Zwei Modi (system_settings.room_category_mode):
//   'standard' (DEHOGA): EZ / DZ / MBZ – Doppelzimmer als eigene Kategorie
//   'simple'  (Vereinfacht): EZ / MBZ – kein DZ, Mehrbettzimmer ab 2 Personen
//
// Der Modus steuert:
//   1. getRoomCategoryLabel()   → Badge-Text auf Raumkarten (RoomPropertyBadges)
//   2. getActivePreferenceKeys() → welche Optionen in Dropdowns erscheinen
//   3. Auto-Assign canAdd()     → Präferenz-Matching gegen Raumkapazität
//
// Verwendet von: RoomPropertyBadges, formatUtils (resolveRoomPreferenceConfig,
// ROOM_PREFERENCE_OPTIONS), useAutoAssign, Sidebar, ContentToolbar, EventPlanner
// Kontextmenü, SignuperAdd, DateStats, GanttView.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Zimmerkategorie-Modus.
 *   'standard' – DEHOGA: EZ (1 Person) / DZ (2 Personen) / MBZ (3+ Personen)
 *   'simple'   – Vereinfacht: EZ (1 Person) / MBZ (2+ Personen), kein DZ
 */
export type RoomCategoryMode = 'standard' | 'simple';

/**
 * Fallback-Config pro Preference-DB-Wert (0=Egal, 1=Einzelzimmer, 2=Doppelzimmer, 3=Mehrbettzimmer).
 * Kurzlabel (shortLabel) wird in kompakten Badges angezeigt (EZ/DZ/MBZ/Egal).
 *
 * Zimmerkategorie nach DEHOGA = vorgesehene Belegung (maxPersons), nicht Bettenart.
 * Im vereinfachten Modus ('simple') wird Key 2 (DZ) ausgeblendet und wie MBZ behandelt.
 */
export const ROOM_PREFERENCE_DEFAULTS: Record<number, SpecialTypeConfig & { shortLabel: string }> = {
  0: { label: 'Egal',            shortLabel: 'Egal', color: 'var(--color-gray)', icon: 'bed' },
  1: { label: 'Einzelzimmer',    shortLabel: 'EZ',   color: 'var(--color-blue)', icon: 'bed-single' },
  2: { label: 'Doppelzimmer',    shortLabel: 'DZ',   color: 'var(--color-amber)', icon: 'bed-double' },
  3: { label: 'Mehrbettzimmer',  shortLabel: 'MBZ',  color: 'var(--color-green)', icon: 'users' },
};

/**
 * Leitet die Zimmerkategorie (EZ/DZ/MBZ) aus maxPersons und dem Kategorie-Modus ab.
 * DEHOGA: Kategorie = vorgesehene Belegung, nicht Bettenart.
 *
 * @param maxPersons  Maximale Personenzahl des Raums (capacity)
 * @param mode        'standard' (EZ/DZ/MBZ) oder 'simple' (EZ/MBZ)
 * @returns           'EZ' | 'DZ' | 'MBZ' | null (null wenn maxPersons <= 0)
 */
export function getRoomCategoryLabel(maxPersons: number, mode: RoomCategoryMode = 'standard'): string | null {
  if (maxPersons <= 0) return null;
  if (maxPersons === 1) return 'EZ';
  if (mode === 'standard' && maxPersons === 2) return 'DZ';
  return 'MBZ';
}

/**
 * Gibt die aktiven Preference-Keys fuer Dropdowns zurueck (modus-abhaengig).
 * Standard: [0, 1, 2, 3] (Egal/EZ/DZ/MBZ)
 * Vereinfacht: [0, 1, 3] (Egal/EZ/MBZ – kein DZ)
 */
export function getActivePreferenceKeys(mode: RoomCategoryMode = 'standard'): number[] {
  if (mode === 'simple') return [0, 1, 3];
  return [0, 1, 2, 3];
}

// ═══════════════════════════════════════════════════════════════════════════════
// Visuelle Identitaet: Altersgruppe – Fallback-Defaults
// ═══════════════════════════════════════════════════════════════════════════════
//
// Keys = DB-Werte (system_dates_signupers.age_group TINYINT).
// Farben referenzieren Hue-Tokens → deriveColorSet() → CSS color-mix() → theme-aware.
//
// Verwendet von: ItemChip (Badge), Sidebar (Filter), AddSignuperPopup, GuiElements.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Fallback-Config pro Altersgruppe-DB-Wert (0=Kind, 1=Jugendlich, 2=Erwachsen).
 * Kurzlabel (shortLabel) wird in kompakten Badges angezeigt (K/J/E).
 */
export const AGE_GROUP_DEFAULTS: Record<number, SpecialTypeConfig & { shortLabel: string }> = {
  0: { label: 'Kind',         shortLabel: 'K', color: 'var(--color-green)', icon: 'baby' },
  1: { label: 'Jugendlich',   shortLabel: 'J', color: 'var(--color-amber)', icon: 'graduation-cap' },
  2: { label: 'Erwachsen',    shortLabel: 'E', color: 'var(--color-indigo)', icon: 'user' },
};

// ═══════════════════════════════════════════════════════════════════════════════
// Visuelle Identitaet: Blocktyp (BlockType) – Fallback-Defaults
// ═══════════════════════════════════════════════════════════════════════════════
//
// Keys = DB-Werte (system_dates_timeblocks.block_type, system_roomtypes.default_block_type).
// Farben referenzieren Hue-Tokens → deriveColorSet() → CSS color-mix() → theme-aware.
// Icon dient als Fallback wenn der Raumtyp kein eigenes Icon hat.
//
// Verwendet von: RoomPropertyBadges (BlockTypeBadge), TimeBlockCard
// (Icon-Fallback), constants.tsx (Labels, Select-Options).
// ═══════════════════════════════════════════════════════════════════════════════

import type { BlockType } from '@/types';

/**
 * Fallback-Config pro Blocktyp.
 * Farben orientieren sich am bisherigen Design: grau/violett/amber.
 */
/**
 * Fester Fallback fuer Termine ohne Termintyp (fk_IdDateType IS NULL).
 * Neutral-grau, damit sofort erkennbar ist, dass kein Typ zugewiesen ist.
 */
export const DATE_TYPE_FALLBACK: SpecialTypeConfig = {
  label: 'Ohne Termintyp', color: 'var(--color-gray)', icon: null,
};

/**
 * Fester Fallback fuer Raeume/Bloecke ohne Raumtyp (fk_IdRoomType IS NULL).
 * Neutral-grau, damit sofort erkennbar ist, dass kein Typ zugewiesen ist.
 */
export const ROOM_TYPE_FALLBACK: SpecialTypeConfig = {
  label: 'Kein Raumtyp', color: 'var(--color-gray)', icon: null,
};

export const BLOCK_TYPE_DEFAULTS: Record<BlockType, SpecialTypeConfig> = {
  mainblock:   { label: 'Hauptblock',     color: 'var(--color-gray)', icon: 'course-room' },
  sleepblock:  { label: 'Schlafblock',    color: 'var(--color-purple)', icon: 'bed' },
  globalblock: { label: 'Globaler Block', color: 'var(--color-amber)', icon: 'clock' },
};

// ═══════════════════════════════════════════════════════════════════════════════
// Scheduling-Farben – Zentrale Farbquelle für alle Zeit-Visualisierungen
// ═══════════════════════════════════════════════════════════════════════════════
//
// Einzige Wahrheitsquelle für Farben in SyncBar, XenoGantt und zukünftigem Kalender.
// Werte referenzieren CSS-Variablen (Design-Tokens) → reagieren auf Theme-Änderungen.
//
// Verwendet von:
//   - EventPlanner/cards/SyncBar.tsx (via SYNC_BAR_COLORS / SYNC_BAR_HEADER_COLORS)
//   - XenoGantt/XenoGanttChart.tsx (Tages-Highlights, Fallback-Farbe, Gap-Textfarbe)
//   - EventPlanner/views/GanttView.tsx (Booking-Fallback-Farbe)
//   - XenoGantt/gantt.css (Gap-Linie zwischen Segmenten – selber Token, direkt in CSS)
//
// KOPPLUNG: gantt.css referenziert dieselben schedule-* Tokens direkt (CSS kann kein JS
// importieren). Beide Seiten nutzen var(--color-schedule-*) → Änderungen via DesignTokenPanel
// wirken automatisch in JS und CSS.
//
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Zentrale Scheduling-Farben für alle Zeit-Visualisierungen.
 * Jeder Wert ist ein CSS-Farbwert (meist `var(--color-*)` Token).
 * SyncBar, XenoGantt und Kalender leiten ihre Farben hieraus ab.
 */
export const SCHEDULING_COLORS = {
  /** Segment ohne Zuordnung – neutrales Grau (SyncBar-Fallback, Gantt-Booking-Fallback). */
  segmentFallback: 'var(--color-schedule-fallback)',
  /** Segment mit eigenen Zeiten (MAN-Modus) – Grün. */
  segmentOwnTimes: 'var(--color-schedule-own-times)',
  /** Ausnahme-Segment – Rot. Auch in gantt.css für Schraffur (var(--color-schedule-exception)). */
  segmentException: 'var(--color-schedule-exception)',
  /** Lücken-Linie zwischen Segmenten (gepunktet/gestrichelt). Auch in gantt.css direkt. */
  gapLine: 'var(--color-schedule-gap)',
  /** An-/Abreise-Marker und -Zonen. */
  marker: 'var(--color-schedule-marker)',
  /** Wochenend-Spalten-Highlight (Gantt, Kalender). */
  weekend: 'var(--color-schedule-weekend)',
  /** Heute-Spalten-Highlight (Gantt, Kalender). */
  today: 'var(--color-schedule-today)',
  /** Auswahl-Highlight – abgeleitet von primary (Gantt, Kalender). */
  selectedDay: 'var(--color-schedule-selected)',
} as const;

export type SyncBarColorSet = {
  /** Hintergrund des Balken-Tracks. */
  trackBackground: string;
  segmentFallback: string;
  segmentOwnTimes: string;
  segmentException: string;
  markerArrivalDeparture: string;
  gapLine: string;
};

/** Farben für die normale SyncBar (Karten: Person, Preis, Extra). */
export const SYNC_BAR_COLORS: SyncBarColorSet = {
  trackBackground: 'transparent',
  segmentFallback: SCHEDULING_COLORS.segmentFallback,
  segmentOwnTimes: SCHEDULING_COLORS.segmentOwnTimes,
  segmentException: SCHEDULING_COLORS.segmentException,
  markerArrivalDeparture: SCHEDULING_COLORS.marker,
  gapLine: SCHEDULING_COLORS.gapLine,
};

/** Farben für die SyncBar im Zeitblock-Header (embedded). */
export const SYNC_BAR_HEADER_COLORS: SyncBarColorSet = {
  ...SYNC_BAR_COLORS,
  trackBackground: 'color-mix(in srgb, var(--color-surface-muted) 80%, transparent)',
  gapLine: 'transparent',
};
